/**
 * BrokerOnboarding.jsx
 *
 * UK broker engagement workflow — separate flow from customer onboarding.
 *  1. Search Companies House for broker legal entity
 *  2. Set commission rates (Initial + Renewal)
 *  3. Sign-on-behalf signatories (Paul Richards pre-filled SF side)
 *  4. Generate Broker Engagement Letter via DocuPilot (template 107249) +
 *     launch existing DocuSign envelope flow for tagging + send.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import BankCoPCheck from "./BankCoPCheck";
import { DocuSignEnvelopesPanel } from "./DocuSignEnvelopesPanel";
import {
  Plus,
  Search,
  Building2,
  Loader2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  Send,
  Handshake,
  Percent,
  RefreshCw,
  FileText,
} from "lucide-react";

const DEFAULT_INITIAL_COMMISSION = 10;
const DEFAULT_RENEWAL_COMMISSION = 6;
const PAUL_RICHARDS = { name: "Paul Richards", email: "paul.richards@simplyfunded.co.uk" };

export default function BrokerOnboarding() {
  // History (rendered as a DocuSign-style envelope panel)
  const [engagements, setEngagements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [brokerEnvelopes, setBrokerEnvelopes] = useState([]);
  const [envelopesError, setEnvelopesError] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);

  // Step 1: Companies House search
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);     // { company_number, title }
  const [brokerDetails, setBrokerDetails] = useState(null);         // full CH profile
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Step 2: Commissions
  const [initialCommission, setInitialCommission] = useState(DEFAULT_INITIAL_COMMISSION);
  const [renewalCommission, setRenewalCommission] = useState(DEFAULT_RENEWAL_COMMISSION);

  // Step 3: Signatories
  const [sfSignatory, setSfSignatory] = useState({ ...PAUL_RICHARDS });
  const [brokerSignatory, setBrokerSignatory] = useState({ name: "", email: "" });

  // Step 4: Broker bank details (CoP optional — brokers don't require it)
  const [brokerBankDetails, setBrokerBankDetails] = useState(null);
  const [bankSkipped, setBankSkipped] = useState(false);

  // Step 4: Generate + DocuSign
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);            // { file_url, file_base64, ... }
  const [engagementId, setEngagementId] = useState(null);
  const [sendingToDocuSign, setSendingToDocuSign] = useState(false);
  const [docuSignResult, setDocuSignResult] = useState(null);
  const [generateError, setGenerateError] = useState(null);          // Persistent error on Step 4

  // Load history on mount
  useEffect(() => {
    loadHistory();
    loadBrokerEnvelopes();
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get("/brokers/engagement-letter/history?limit=50");
      setEngagements(res.data.engagements || []);
    } catch (e) {
      console.error("loadHistory failed", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Broker DocuSign envelopes — same rich view as the customer centre.
  // Filtered from the UK envelope list to Broker Engagement Letters.
  const loadBrokerEnvelopes = async () => {
    setLoadingHistory(true);
    setEnvelopesError(null);
    try {
      const res = await api.get("/contracts/envelopes/list", { params: { region: "uk", days: 365 } });
      if (res.data?.success) {
        const broker = (res.data.envelopes || []).filter((e) =>
          (e.subject || "").toLowerCase().includes("broker engagement")
        );
        setBrokerEnvelopes(broker);
      } else {
        setEnvelopesError(res.data?.error || "Could not load envelopes");
      }
    } catch (e) {
      console.error("loadBrokerEnvelopes failed", e);
      setEnvelopesError(e?.response?.data?.detail || "Could not load broker envelopes");
    } finally {
      setLoadingHistory(false);
    }
  };

  const refreshAll = () => { loadHistory(); loadBrokerEnvelopes(); };

  // ---------- Step 1 helpers ----------
  const searchCompaniesHouse = async () => {
    if (!companySearch || companySearch.length < 2) return;
    setSearching(true);
    setCompanyResults([]);
    try {
      const res = await api.get(`/companies-house/search?q=${encodeURIComponent(companySearch)}&limit=15`);
      setCompanyResults(res.data.companies || res.data.items || []);
    } catch (e) {
      console.error(e);
      toast.error(errorDetailMessage(e, "Companies House search failed"));
    } finally {
      setSearching(false);
    }
  };

  const selectCompany = async (company) => {
    setSelectedCompany(company);
    setLoadingDetails(true);
    setBrokerDetails(null);
    try {
      const res = await api.get(`/companies-house/company/${company.company_number}`);
      setBrokerDetails(res.data);
    } catch (e) {
      console.error(e);
      toast.error(errorDetailMessage(e, "Failed to load company details"));
    } finally {
      setLoadingDetails(false);
    }
  };

  // ---------- Wizard navigation ----------
  const resetWizard = () => {
    setStep(1);
    setCompanySearch("");
    setCompanyResults([]);
    setSelectedCompany(null);
    setBrokerDetails(null);
    setInitialCommission(DEFAULT_INITIAL_COMMISSION);
    setRenewalCommission(DEFAULT_RENEWAL_COMMISSION);
    setSfSignatory({ ...PAUL_RICHARDS });
    setBrokerSignatory({ name: "", email: "" });
    setBrokerBankDetails(null);
    setBankSkipped(false);
    setGeneratedDoc(null);
    setEngagementId(null);
    setDocuSignResult(null);
    setGenerateError(null);
  };

  const openWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const canProceed = () => {
    if (step === 1) return !!brokerDetails;
    if (step === 2) return initialCommission >= 0 && renewalCommission >= 0;
    if (step === 3)
      return (
        !!sfSignatory.name &&
        !!sfSignatory.email &&
        !!brokerSignatory.name &&
        !!brokerSignatory.email
      );
    if (step === 4) return bankSkipped || !!brokerBankDetails?.verified;
    return true;
  };

  // ---------- Generate the engagement letter via DocuPilot ----------
  const formatAddress = (details) => {
    const a = details?.registered_office_address || {};
    return [a.address_line_1, a.address_line_2, a.locality, a.region, a.postal_code]
      .filter(Boolean)
      .join(", ");
  };

  const generateLetter = async () => {
    if (!brokerDetails) return;
    setGenerating(true);
    setGeneratedDoc(null);
    setGenerateError(null);
    try {
      const payload = {
        broker_company_number: brokerDetails.company_number,
        broker_company_name: brokerDetails.company_name,
        broker_registered_address: formatAddress(brokerDetails),
        broker_commission: parseFloat(initialCommission) || 0,
        renewal_commission: parseFloat(renewalCommission) || 0,
        sf_signatory: sfSignatory,
        broker_signatory: brokerSignatory,
        bank_details: brokerBankDetails || null,
      };
      const res = await api.post("/brokers/engagement-letter/generate", payload);
      if (res.data.success) {
        setGeneratedDoc(res.data.document);
        setEngagementId(res.data.broker_engagement_id);
        toast.success("Broker Engagement Letter generated");
        refreshAll();
      } else {
        const msg = "Generation failed";
        setGenerateError(msg);
        toast.error(msg);
      }
    } catch (e) {
      console.error(e);
      const detail =
        e.response?.data?.detail ||
        e.message ||
        "Failed to generate document";
      // Surface DocuPilot's specific token error verbatim so ops know what to fix.
      setGenerateError(detail);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  // ---------- Send via DocuSign (reuses /contracts/send-to-docusign) ----------
  const sendToDocuSign = async () => {
    if (!generatedDoc || !engagementId) return;
    setSendingToDocuSign(true);
    try {
      const payload = {
        application_id: `BROKER-${engagementId}`,
        company_name: brokerDetails.company_name,
        documents: [{
          type: "Broker Engagement Letter",
          file_url: generatedDoc.file_url,
          file_base64: generatedDoc.file_base64,
          filename: generatedDoc.file_name,
          content_type: "application/pdf",
        }],
        signers: [
          {
            name: sfSignatory.name,
            email: sfSignatory.email,
            role: "lender_signatory",
          },
          {
            name: brokerSignatory.name,
            email: brokerSignatory.email,
            role: "broker_signatory",
          },
        ],
        email_subject: `Broker Engagement Letter — ${brokerDetails.company_name}`,
        email_message:
          "Please review and sign the SimplyFunded Broker Engagement Letter. Thank you.",
        sent_by: "broker_onboarding",
        region: "uk",
      };
      const res = await api.post("/contracts/send-to-docusign", payload);
      if (res.data.success) {
        setDocuSignResult(res.data);
        if (res.data.envelope_id) {
          await api.post(
            `/brokers/engagement-letter/${engagementId}/link-envelope?envelope_id=${encodeURIComponent(res.data.envelope_id)}&status=sent`
          );
          refreshAll();
        }
        if (res.data.sender_view_url) {
          window.open(res.data.sender_view_url, "_blank", "noopener");
          toast.success("DocuSign tagging window opened — finish in the new tab.");
        } else {
          toast.success("DocuSign envelope created");
        }
      } else {
        toast.error("DocuSign send failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "DocuSign send failed");
    } finally {
      setSendingToDocuSign(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="space-y-6" data-testid="broker-onboarding-root">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Handshake className="w-5 h-5" />
            Broker Onboarding
          </h2>
          <p className="text-sm text-muted-foreground">
            Generate &amp; send a Broker Engagement Letter via DocuPilot + DocuSign.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={loadingHistory} data-testid="broker-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingHistory ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={openWizard} data-testid="new-broker-onboarding-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Broker Onboarding
          </Button>
        </div>
      </div>

      {/* History — DocuSign envelopes panel (same format as the customer centre) */}
      <DocuSignEnvelopesPanel
        envelopes={brokerEnvelopes}
        loading={loadingHistory}
        error={envelopesError}
        onRefresh={loadBrokerEnvelopes}
        locale="en-GB"
      />

      {/* Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl" data-testid="broker-wizard-dialog">
          <DialogHeader>
            <DialogTitle>New Broker Onboarding</DialogTitle>
            <DialogDescription>
              Step {step} of 5 — {stepLabel(step)}
            </DialogDescription>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center gap-2 mb-2" data-testid="broker-stepper">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="flex-1 flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                    step >= n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {step > n ? <CheckCircle className="w-4 h-4" /> : n}
                </div>
                {n < 5 && (
                  <div
                    className={`h-1 flex-1 rounded ${step > n ? "bg-blue-500" : "bg-slate-200"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1 — Broker Entity */}
          {step === 1 && (
            <div className="space-y-4" data-testid="broker-step-1">
              <Label>Search Companies House for the Broker's legal entity</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. ACME Broker Ltd or company number"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchCompaniesHouse()}
                  data-testid="broker-ch-search-input"
                />
                <Button
                  onClick={searchCompaniesHouse}
                  disabled={searching || companySearch.length < 2}
                  data-testid="broker-ch-search-btn"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {companyResults.length > 0 && (
                <div className="border rounded-lg max-h-60 overflow-auto">
                  {companyResults.map((c) => (
                    <button
                      key={c.company_number}
                      onClick={() => selectCompany(c)}
                      className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-slate-50 ${
                        selectedCompany?.company_number === c.company_number ? "bg-blue-50" : ""
                      }`}
                      data-testid={`broker-ch-result-${c.company_number}`}
                    >
                      <div className="font-medium text-sm">{c.title || c.company_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.company_number} · {c.company_status}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {loadingDetails && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading company profile…
                </div>
              )}

              {brokerDetails && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 space-y-1 text-sm">
                    <div className="flex items-center gap-2 font-medium">
                      <Building2 className="w-4 h-4" />
                      {brokerDetails.company_name}
                      <Badge variant="secondary" className="ml-auto bg-green-100 text-green-700">
                        Selected
                      </Badge>
                    </div>
                    <div className="text-muted-foreground">
                      <span className="font-medium">Company #:</span> {brokerDetails.company_number}
                    </div>
                    <div className="text-muted-foreground">
                      <span className="font-medium">Address:</span> {formatAddress(brokerDetails)}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2 — Commission rates */}
          {step === 2 && (
            <div className="space-y-4" data-testid="broker-step-2">
              <p className="text-sm text-muted-foreground">
                These percentages appear in the Commission Structure section of the engagement letter.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Initial Funding Advance %</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.1"
                      min={0}
                      max={100}
                      value={initialCommission}
                      onChange={(e) => setInitialCommission(e.target.value)}
                      className="pr-8"
                      data-testid="broker-initial-commission"
                    />
                    <Percent className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: {DEFAULT_INITIAL_COMMISSION}%
                  </p>
                </div>
                <div>
                  <Label>Renewal Funding Advance %</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.1"
                      min={0}
                      max={100}
                      value={renewalCommission}
                      onChange={(e) => setRenewalCommission(e.target.value)}
                      className="pr-8"
                      data-testid="broker-renewal-commission"
                    />
                    <Percent className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: {DEFAULT_RENEWAL_COMMISSION}%
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Signatories */}
          {step === 3 && (
            <div className="space-y-5" data-testid="broker-step-3">
              <div className="space-y-2">
                <Label className="font-medium">SimplyFunded signatory</Label>
                <p className="text-xs text-muted-foreground">
                  Defaults to Paul Richards. Edit if someone else is signing on behalf of SimplyFunded.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Full name"
                    value={sfSignatory.name}
                    onChange={(e) => setSfSignatory((p) => ({ ...p, name: e.target.value }))}
                    data-testid="broker-sf-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={sfSignatory.email}
                    onChange={(e) => setSfSignatory((p) => ({ ...p, email: e.target.value }))}
                    data-testid="broker-sf-email"
                  />
                </div>
              </div>

              <div className="space-y-2 border-t pt-4">
                <Label className="font-medium">Broker signatory</Label>
                <p className="text-xs text-muted-foreground">
                  The director who will sign on behalf of {brokerDetails?.company_name || "the broker"}.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Full name"
                    value={brokerSignatory.name}
                    onChange={(e) => setBrokerSignatory((p) => ({ ...p, name: e.target.value }))}
                    data-testid="broker-counterparty-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={brokerSignatory.email}
                    onChange={(e) => setBrokerSignatory((p) => ({ ...p, email: e.target.value }))}
                    data-testid="broker-counterparty-email"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Broker Bank Details (CoP OPTIONAL for brokers) */}
          {step === 4 && (
            <div className="space-y-4" data-testid="broker-step-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="font-medium">Broker bank account <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <p className="text-xs text-muted-foreground">
                    Confirmation of Payee isn't required for brokers. Verify if you want to,
                    or skip this step.
                  </p>
                </div>
                {!bankSkipped && !brokerBankDetails?.verified && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBankSkipped(true)}
                    data-testid="broker-skip-bank-btn"
                  >
                    Not needed — skip
                  </Button>
                )}
              </div>

              {bankSkipped ? (
                <div className="p-4 bg-slate-50 border rounded-lg text-sm flex items-center justify-between">
                  <span className="text-muted-foreground">Bank verification skipped — not required for brokers.</span>
                  <Button variant="ghost" size="sm" onClick={() => setBankSkipped(false)} data-testid="broker-unskip-bank-btn">
                    Add bank details
                  </Button>
                </div>
              ) : (
                <>
                  {brokerBankDetails?.verified && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm flex items-center gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4" />
                      Verified: {brokerBankDetails.account_name} · {brokerBankDetails.sort_code} · {brokerBankDetails.account_number}
                      {brokerBankDetails.bank_name ? ` · ${brokerBankDetails.bank_name}` : ""}
                    </div>
                  )}
                  <BankCoPCheck
                    defaultAccountName={brokerDetails?.company_name || ""}
                    onVerified={(bd) => setBrokerBankDetails(bd)}
                  />
                </>
              )}
            </div>
          )}

          {/* Step 5 — Generate + DocuSign */}
          {step === 5 && (
            <div className="space-y-4" data-testid="broker-step-5">
              <Card className="bg-slate-50">
                <CardContent className="p-4 text-sm space-y-1">
                  <div><strong>Broker:</strong> {brokerDetails?.company_name} ({brokerDetails?.company_number})</div>
                  <div><strong>Initial commission:</strong> {initialCommission}%</div>
                  <div><strong>Renewal commission:</strong> {renewalCommission}%</div>
                  <div><strong>SF signatory:</strong> {sfSignatory.name} ({sfSignatory.email})</div>
                  <div><strong>Broker signatory:</strong> {brokerSignatory.name} ({brokerSignatory.email})</div>
                  {brokerBankDetails?.verified && (
                    <div><strong>Bank (CoP):</strong> {brokerBankDetails.account_name} · {brokerBankDetails.sort_code} · {brokerBankDetails.account_number}</div>
                  )}
                </CardContent>
              </Card>

              {!generatedDoc ? (
                <>
                  <Button
                    onClick={generateLetter}
                    disabled={generating}
                    className="w-full"
                    data-testid="broker-generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating via DocuPilot…
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate Engagement Letter
                      </>
                    )}
                  </Button>
                  {generateError && (
                    <Card
                      className="bg-red-50 border-red-200 mt-3"
                      data-testid="broker-generate-error"
                    >
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center gap-2 text-red-800 font-medium">
                          <FileText className="w-4 h-4" />
                          Generation failed
                        </div>
                        <div className="text-sm text-red-700 break-words whitespace-pre-wrap">
                          {generateError}
                        </div>
                        {/^(.*Template tokens.*|.*invalid.*)$/i.test(generateError) && (
                          <div className="text-xs text-red-600 bg-red-100 rounded p-2 mt-2">
                            <strong>Likely cause:</strong> DocuPilot template{" "}
                            <code>107249</code> has empty / invalid merge tokens. Open
                            the template in DocuPilot and confirm the 10 tokens
                            (AgreementDate, BrokerCompanyName, BrokerCompanyNumber,
                            BrokerRegisteredAddress, BrokerCommission,
                            RenewalCommission, SFSignatory, SFSignature,
                            BrokerSignatory, BrokerSignature) are present, then try
                            again.
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGenerateError(null)}
                          className="mt-1"
                          data-testid="broker-dismiss-error"
                        >
                          Dismiss
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-green-800 font-medium">
                      <CheckCircle className="w-5 h-5" />
                      Engagement letter generated
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(generatedDoc.file_url, "_blank", "noopener")}
                        data-testid="broker-preview-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Preview / Download
                      </Button>
                      <Button
                        size="sm"
                        onClick={sendToDocuSign}
                        disabled={sendingToDocuSign || !!docuSignResult}
                        data-testid="broker-send-docusign-btn"
                      >
                        {sendingToDocuSign ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-1" />
                            {docuSignResult ? "DocuSign envelope created" : "Send via DocuSign"}
                          </>
                        )}
                      </Button>
                    </div>
                    {docuSignResult?.envelope_id && (
                      <div className="text-xs text-green-700 break-all">
                        Envelope ID: {docuSignResult.envelope_id}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              data-testid="broker-wizard-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < 5 ? (
              <Button
                onClick={() => setStep((s) => Math.min(5, s + 1))}
                disabled={!canProceed()}
                data-testid="broker-wizard-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setWizardOpen(false)}
                data-testid="broker-wizard-done"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function stepLabel(s) {
  return ["Broker entity", "Commission rates", "Signatories", "Bank details", "Generate & send"][s - 1];
}

function statusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "generated":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "sent":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "completed":
      return "bg-green-50 text-green-700 border-green-200";
    case "voided":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "";
  }
}
